import { Course, MaterialType } from '@/client/courses';
import { QuizType } from '@/client/quizzes';
import { Actions, Subjects, SystemRoles } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { UnsavedChangesModal } from '@/components/courses/modals/UnsavedChangesModal';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { hasRestrictedUserScope } from '@/utils/custom-roles';
import {
  canPreviewContent,
  hasTranslationAccess,
  updateMaterialByTypePath,
} from '@/utils/helpers';
import classNames from 'classnames';
import { MenuItem } from 'primereact/menuitem';
import { TabMenu } from 'primereact/tabmenu';
import { Tooltip } from 'primereact/tooltip';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

type CoursesTabsProps = {
  course: Course;
  hasErrors?: boolean;
  hasUnsavedChanges?: boolean;
  onSave?: () => Promise<void>;
};

const getItemTemplate =
  (
    url: string,
    current: string,
    onTabClick: (url: string) => void,
    tooltipId?: string,
  ) =>
  (item: MenuItem) =>
    (
      <a
        id={tooltipId}
        href="#"
        onClick={(e) => {
          e.preventDefault();
          if (item.disabled) {
            return;
          }
          onTabClick(url);
        }}
        className={classNames({
          'p-menuitem-link': true,
          'p-menuitem-link-active': current.includes(url),
          'p-disabled': item.disabled,
        })}
        role="menuitem"
        style={
          item.disabled && tooltipId
            ? { pointerEvents: 'auto', cursor: 'not-allowed' }
            : undefined
        }
      >
        <span className="p-menuitem-text">{item.label}</span>
      </a>
    );

export const CoursesTabs: React.FC<CoursesTabsProps> = ({
  course,
  hasErrors = false,
  hasUnsavedChanges = false,
  onSave,
}) => {
  const { t } = useTranslation();
  const { can } = usePermission();
  const location = useLocation();
  const navigate = useNavigate();
  const account = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const isMaterial = course.type !== MaterialType.COURSE;
  const isResource = course.type === MaterialType.RESOURCE;
  const isPoster = course.type === MaterialType.POSTER;
  const isCustomMaterial = course.type === MaterialType.CUSTOM_MATERIAL;
  const courseQuiz = course.courseContents?.find(
    (content) => content?.quiz?.type === QuizType.TRADITIONAL,
  )?.quiz;

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);

  const handleTabClick = (url: string) => {
    if (hasUnsavedChanges) {
      setPendingUrl(url);
      setShowUnsavedModal(true);
    } else {
      navigate(url);
    }
  };

  const handleModalSave = async () => {
    if (onSave) {
      await onSave();
    }
    if (pendingUrl) {
      navigate(pendingUrl);
    }
    setShowUnsavedModal(false);
    setPendingUrl(null);
  };

  const handleModalDiscard = () => {
    if (pendingUrl) {
      navigate(pendingUrl);
    }
    setShowUnsavedModal(false);
    setPendingUrl(null);
  };

  const handleModalHide = () => {
    setShowUnsavedModal(false);
    setPendingUrl(null);
  };

  const items: MenuItem[] = [];
  const courseId = course.id;
  const type = course.type;

  if (
    (!isMaterial || isCustomMaterial) &&
    canPreviewContent(course, currentUser)
  ) {
    const previewPath = isCustomMaterial
      ? RedirectPaths[RedirectPathsEnum.CUSTOM_MATERIALS_PREVIEW](course.id)
      : RedirectPaths[RedirectPathsEnum.COURSES_SCORM_PREVIEW](course.id);

    items.push({
      label: t('content.preview'),
      template: getItemTemplate(previewPath, location.pathname, handleTabClick),
      data: previewPath,
    });
  }

  items.push({
    label: t('content.tab'),
    template: getItemTemplate(
      isMaterial
        ? updateMaterialByTypePath(type, courseId)
        : RedirectPaths[RedirectPathsEnum.COURSES_CONTENT](courseId),
      location.pathname,
      handleTabClick,
    ),
    data: isMaterial
      ? updateMaterialByTypePath(type, courseId)
      : RedirectPaths[RedirectPathsEnum.COURSES_CONTENT](courseId),
  });

  if (account?.isSystem) {
    if (!isPoster) {
      items.push({
        label: t('generic.schedule'),
        template: getItemTemplate(
          isMaterial
            ? RedirectPaths[RedirectPathsEnum.RESOURCES_SCHEDULE](course.id)
            : RedirectPaths[RedirectPathsEnum.COURSES_SCHEDULE](course.id),
          location.pathname,
          handleTabClick,
        ),
        data: isMaterial
          ? RedirectPaths[RedirectPathsEnum.RESOURCES_SCHEDULE](course.id)
          : RedirectPaths[RedirectPathsEnum.COURSES_SCHEDULE](course.id),
      });
    }

    if (
      can(Actions.READ, Subjects.COURSE_ACCOUNT_SCHEDULES) &&
      !isCustomMaterial
    ) {
      items.push({
        label: t('accounts'),
        template: getItemTemplate(
          isResource
            ? RedirectPaths[RedirectPathsEnum.RESOURCES_ACCOUNTS](course.id)
            : isPoster
            ? RedirectPaths[RedirectPathsEnum.POSTER_ACCOUNTS](course.id)
            : RedirectPaths[RedirectPathsEnum.COURSES_ACCOUNTS](course.id),
          location.pathname,
          handleTabClick,
        ),
        data: isResource
          ? RedirectPaths[RedirectPathsEnum.RESOURCES_ACCOUNTS](course.id)
          : isPoster
          ? RedirectPaths[RedirectPathsEnum.POSTER_ACCOUNTS](course.id)
          : RedirectPaths[RedirectPathsEnum.COURSES_ACCOUNTS](course.id),
      });
    }
  }

  if (account?.isSystem && !isMaterial) {
    items.push({
      label: t('analytics'),
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.COURSES_ANALYTICS](course.id),
        location.pathname,
        handleTabClick,
      ),
      data: RedirectPaths[RedirectPathsEnum.COURSES_ANALYTICS](course.id),
    });
  }

  if (
    !account?.isSystem &&
    can(Actions.UPDATE, Subjects.COURSE_USER_SCHEDULES) &&
    !isPoster
  ) {
    items.push({
      label: t('users'),
      template: getItemTemplate(
        isMaterial
          ? RedirectPaths[
              isResource
                ? RedirectPathsEnum.RESOURCES_USERS
                : RedirectPathsEnum.CUSTOM_MATERIALS_USERS
            ](course.id)
          : RedirectPaths[RedirectPathsEnum.COURSES_USERS](course.id),
        location.pathname,
        handleTabClick,
      ),
      data: isMaterial
        ? RedirectPaths[
            isResource
              ? RedirectPathsEnum.RESOURCES_USERS
              : RedirectPathsEnum.CUSTOM_MATERIALS_USERS
          ](course.id)
        : RedirectPaths[RedirectPathsEnum.COURSES_USERS](course.id),
    });
  }

  const canSeeQuizAnswers =
    can(Actions.READ, Subjects.ACCOUNT_REPORTS) &&
    !hasRestrictedUserScope(currentUser?.customRole?.scopeMode);

  if (!account?.isSystem && !isMaterial && canSeeQuizAnswers) {
    items.push({
      label: t('results'),
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.COURSES_RESULTS](
          course.id,
          courseQuiz?.id ?? '',
        ),
        location.pathname,
        handleTabClick,
        `results-tab-${course.id}`,
      ),
      data: RedirectPaths[RedirectPathsEnum.COURSES_RESULTS](
        course.id,
        courseQuiz?.id ?? '',
      ),
      disabled: !courseQuiz?.id,
    });
  }

  // Add Changes tab for customized courses
  if (!isMaterial && course.customizationMetadata?.originalContentId) {
    items.push({
      label: t('course.changes.tab'),
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.COURSES_CHANGES](courseId),
        location.pathname,
        handleTabClick,
      ),
      data: RedirectPaths[RedirectPathsEnum.COURSES_CHANGES](courseId),
    });
  }

  if (
    hasTranslationAccess(
      account?.id || '',
      currentUser?.role.code as SystemRoles,
      course,
      can,
      account?.isSystem,
    )
  ) {
    items.push({
      label: t('course.translation.tab'),
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.COURSES_TRANSLATE](courseId),
        location.pathname,
        handleTabClick,
      ),
      data: RedirectPaths[RedirectPathsEnum.COURSES_TRANSLATE](courseId),
    });
  }

  const paths = items.map((item) => item.data);
  const currentPath = location.pathname;

  const [activeIndex, setActiveIndex] = useState<number>(
    paths.indexOf(currentPath),
  );

  return (
    <>
      <TabMenu
        model={items}
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
        className="mt-5 flex-shrink-0"
      />
      {!courseQuiz?.id && (
        <Tooltip
          target={`#results-tab-${course.id}`}
          content={t('results.noQuiz')}
          position="right"
          my="left+10 center"
        />
      )}
      <UnsavedChangesModal
        visible={showUnsavedModal}
        onHide={handleModalHide}
        onSave={handleModalSave}
        onDiscard={handleModalDiscard}
        hasErrors={hasErrors}
        title={t('generic.unsavedChanges')}
        message={t('generic.unsavedChanges.message')}
        saveButtonLabel={t('button.save')}
        discardButtonLabel={t('button.continueWithoutSaving')}
      />
    </>
  );
};
